import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { useCounterPrintAgent } from '@/lib/printAgent'
import { desktop } from '@/lib/printTransport'
import { getPrintAgentConfig, subscribePrintAgentConfig } from '@/lib/stationPrinterMap'

/**
 * Mounts the counter print agent (F-FE-N60). Renders nothing.
 *
 * Lives in the admin shell rather than on the counter screen: the counter PC
 * has to keep printing relayed slips while the cashier is on Reports or Day
 * Close, and a waiter at a table has no way of knowing which screen the till
 * happens to be showing.
 *
 * Inert everywhere except a desktop terminal whose operator has switched the
 * agent on — which is every browser, every tablet, and every counter PC that
 * isn't the designated one.
 */
export function CounterPrintAgent() {
  // Reactive, not read-once: flipping the switch in Admin must start the agent
  // on the spot. Reading it once at mount meant the operator had to restart the
  // app before a single ticket was claimed, with tablets queueing paper in the
  // meantime and nothing on screen saying so.
  const [agent, setAgent] = useState(() => getPrintAgentConfig())
  useEffect(() => subscribePrintAgentConfig(() => setAgent(getPrintAgentConfig())), [])

  const shouldRun = !!desktop() && agent.enabled

  // Same key/shape as every other settings reader (['settings','all'] returning
  // the envelope) so this shares their cache instead of forcing a second fetch.
  const { data: settingsRes } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    enabled: shouldRun,
    staleTime: 5 * 60 * 1000,
  })

  const cfg = parseReceiptSettings(settingsRes?.data as Record<string, unknown> | undefined)

  useCounterPrintAgent({
    paperWidthMm: cfg.paperWidthMm,
    printableAreaMm: cfg.printableAreaMm,
    kotFontScale: cfg.kotFontScale,
  })

  return null
}
